import { Locale, enUS, et, ru } from '@teliaee/design-system/locale';
import { action, computed, observable, reaction } from 'mobx';
import CookieApi from '../util/CookieApi';
import RootStore from './RootStore';
import RouteStore from './RouteStore';

export const DEFAULT_LANGUAGE = 'et';
export const ALLOWED_LANGUAGES: string[] = ['et', 'ru', 'en'];

export type Language = 'et' | 'ru' | 'en';

export const GUEST_LANGUAGE_COOKIE_NAME = 'LANGUAGE_EXT';

const LANGUAGE_ID_TO_LOCALE_MAP: { [lang: string]: string } = { et_EE: 'et', ru_RU: 'ru', en_GB: 'en' };
const LOCALE_TO_LANGUAGE_ID_MAP: { [lang: string]: string } = { et: 'et_EE', ru: 'ru_RU', en: 'en_GB' };

class LanguageStore {
  @observable accessor currentLanguage: Language;

  private readonly routeStore: RouteStore;

  constructor(rootStore: RootStore) {
    this.currentLanguage = DEFAULT_LANGUAGE;
    this.routeStore = rootStore.routeStore;
    this.init();
    reaction(
      () => this.routeStore.langFromParams,
      () => this.setLanguageFromRouteStoreParams(),
    );
  }

  @action.bound
  public setLanguage(language: Language) {
    this.currentLanguage = language;
    document.querySelector('html')?.setAttribute('lang', language);
    CookieApi.setLanguageCookie(LOCALE_TO_LANGUAGE_ID_MAP[language]);
  }

  @computed
  public get currentLocale(): Locale {
    switch (this.currentLanguage) {
      case 'en':
        return enUS;
      case 'ru':
        return ru;
      default:
        return et;
    }
  }

  private isValidLang(lang: Language) {
    return lang && ALLOWED_LANGUAGES.includes(lang);
  }

  private init() {
    const locale = this.routeStore.decodedParams.locale;
    if (this.isValidLang(locale)) {
      if (locale !== this.currentLanguage) {
        this.setLanguage(locale as Language);
      }
      return;
    }

    const storedLang = this.getLanguageFromCookie();
    if (storedLang && storedLang !== this.currentLanguage) {
      this.setLanguage(storedLang as Language);
    }
  }

  private resolveLocale = (languageId: string) => {
    return LANGUAGE_ID_TO_LOCALE_MAP[languageId] as Language;
  };

  private getLanguageFromCookie() {
    return this.resolveLocale(CookieApi.getCookieValue(GUEST_LANGUAGE_COOKIE_NAME) as Language);
  }

  private setLanguageFromRouteStoreParams() {
    const lang = this.routeStore.langFromParams as Language;
    if (this.isValidLang(lang) && this.currentLanguage !== lang) {
      this.setLanguage(lang);
    }
  }
}

export default LanguageStore;
